/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

interface FooterLink {
  text: string;
  url: string;
  display_label?: string;
}

interface FooterSection {
  title: string;
  label?: string;
  links: FooterLink[];
}

interface FooterSocialLink {
  name: string;
  url: string;
  icon: string;
}

interface FooterAppStoreLink {
  name: string;
  url: string;
  imageUrl: string;
}

export interface FooterDownloadApp {
  title: string;
  apps: FooterAppStoreLink[];
}

export interface FooterConnectUs {
  title: string;
  icons: FooterSocialLink[];
}

export interface FooterActions {
  downloads: FooterDownloadApp;
  connect: FooterConnectUs;
}

interface FooterLinkDef {
  label: string;
  url: string;
}

export interface FooterDisclaimer {
  text: string;
  links: Record<string, FooterLinkDef>;
}

export interface WebFooterViewProps {
  primaryText: string;
  sections: FooterSection[];
  actions: FooterActions;
  disclaimer: FooterDisclaimer;
  onLinkPress?: (sectionTitle: string, linkText: string) => void;
}

export interface ChinaLicenseItem {
  text: string;
  link: string;
}

export const DEFAULT_CHINA_LICENSE_PERMIT_INFO: ChinaLicenseItem[] = [
  {
    text: '粤ICP备17160544号-2',
    link: 'https://beian.miit.gov.cn#/Integrated/recordQuery',
  },
  {
    text: '证照信息',
    link: 'https://static.airasia.com/mobile/home/footer/AAcom_Business_Licens-20251126.pdf',
  },
];

export const webFooterMock = {
  primaryText: 'Book your Flights, Hotels & SNAP',
  sections: [
    {
      title: 'Travel Information',
      links: [
        {
          text: 'Flight status',
          url: 'https://www.airasia.com/flightstatus/en/GB',
        },
        {
          text: 'Airports and terminals',
          url: 'https://support.airasia.com/s/article/Airports-and-Terminals?language=en_GB',
        },
        {
          text: 'Check-in information',
          url: 'https://support.airasia.com/s/article/What-is-Self-Check-In-en?language=en_GB',
        },
        {
          text: 'Mobile Check-in',
          url: 'https://www.airasia.com/aa/mobile-check-in/index.html',
        },
        {
          text: 'Our fares',
          url: 'https://www.airasia.com/aa/about-us/en/gb/fares.html',
        },
        {
          text: 'Fees and Charges',
          url: 'https://www.airasia.com/aa/about-us/en/gb/fees-and-charges.html',
        },
        {
          text: 'Payment channels',
          url: 'https://support.airasia.com/s/article/How-do-I-pay-for-my-flights-en?language=en_GB',
        },
        { text: 'Tax invoice', url: 'https://taxinvoice.airasia.com/' },
        {
          text: 'Ancillary Add-Ons',
          url: 'https://www.airasia.com/aa/inflight-comforts/',
        },
      ],
    },
    {
      title: 'Our Products and Services',
      links: [
        { text: 'Flights', url: 'https://www.airasia.com/flight/en/gb' },
        { text: 'SNAP', url: 'https://www.airasia.com/snap/en/gb' },
        { text: 'Hotels', url: 'https://www.airasia.com/hotels/en/gb' },
        {
          text: 'Ride/ Airport Transfer',
          url: 'https://www.airasia.com/ride/',
        },
        { text: 'Points exchange', url: 'https://xchange.airasiabig.com/home' },
        {
          text: 'Events & Activities',
          url: 'https://movetix.airasia.com/gb/en/thb',
        },
        { text: 'Duty-free', url: 'https://www.airasia.com/dutyfree/en/gb' },
        { text: 'ASEAN Pass', url: 'https://www.airasia.com/unlimited/en/gb' },
        {
          text: 'Travel Insurance',
          url: 'https://www.tuneprotect.com/airasia',
        },
        {
          text: 'Gifts',
          url: 'https://www.airasia.com/aa/airasiagifts/my/en/',
        },
        {
          text: 'IKHLAS',
          url: 'https://ikhlas.com/en/?utm_source=airasia&utm_medium=footerlink&utm_campaign=ikh-homepage%7C18112020%7Cgb-en',
        },
        {
          text: 'Rewards',
          url: 'https://www.airasia.com/rewards/en/gb?utm_campaign=NA_ALL_MMBS_None_None&utm_medium=referral&utm_source=aa_homepage_footer#waystoearn',
        },
        {
          text: 'Teleport Logistics',
          url: 'https://www.teleport.asia/en/my?utm_source=aa_homepage_footer&utm_medium=referral&utm_campaign=teleport&langCode=en-gb',
        },
        { text: 'BigPay', url: 'https://bigpayme.com/' },
        {
          text: 'Charter',
          url: 'https://docs.google.com/forms/d/e/1FAIpQLScy9UGyXoFQ_UIwQxGJaMNxqowTGEJhH0HO6UDeARJjyK70tg/viewform',
        },
        { text: 'Promotions', url: 'https://www.airasia.com/promotions/' },
      ],
    },
    {
      title: "Partners' Product",
      links: [
        {
          text: 'Our airline partners',
          url: 'https://www.airasia.com/aa/about-us/en/gb/airlines-partners.html',
        },
        {
          text: 'Affiliate Partner',
          url: 'https://www.airasia.com/aa/affiliate-partners/?utm_source=aa_homepage_footer&utm_medium=referral&utm_campaign=affiliate&langCode=en-gb',
        },
        { text: 'AirAsia Card', url: 'https://www.airasiacards.com' },
        {
          text: 'CIMB Savers',
          url: 'https://www.cimb.com.my/en/personal/day-to-day-banking/accounts/savings-account/octosavers-account-i.html',
        },
        {
          text: 'Agent login',
          url: 'https://www.airasia.com/agent/login/en/gb',
        },
        {
          text: 'Register as trade partner',
          url: 'https://www.airasia.com/aa/about-us/en/gb/agent-signup.html',
        },
      ],
    },
    {
      title: 'About Us',
      links: [
        {
          text: 'About AirAsia MOVE',
          url: 'https://www.airasia.com/aa/about-us/en/gb/airasia-move.html',
        },
        {
          text: 'Advertise With Us',
          url: 'https://www.airasia.com/ads/index.html',
        },
        {
          text: 'AirAsia Foundation',
          url: 'https://www.airasiafoundation.com/',
        },
        { text: 'Careers', url: 'https://mycareer.airasia.com/gb/en' },
        { text: 'Investor relations', url: 'https://www.airasia.com/ir/' },
        { text: 'Newsroom', url: 'https://newsroom.airasia.com/' },
      ],
    },
  ],
  actions: {
    downloads: {
      title: 'AirAsia MOVE',
      apps: [
        {
          name: 'app store',
          url: 'https://airasia.onelink.me/F4uy/9a77bcf2',
          imageUrl:
            'https://static.airasia.com/design/images/email/appstore.svg',
        },
        {
          name: 'google play',
          url: 'https://airasia.onelink.me/F4uy/43939242',
          imageUrl:
            'https://static.airasia.com/design/images/email/googleplay.svg',
        },
        {
          name: 'app gallery',
          url: 'https://appgallery.huawei.com/#/app/C100141357?sharePrepath=ag&channelId=appdownload%20page&id=ed8340378fd24f95a75ba333733a6415&s=65873558A5A2C43D4A9B20499A835032C58C68BDBE39B81310ABF4F176EB4C68&detailType=0&v=',
          imageUrl:
            'https://static.airasia.com/design/images/email/appgallery.svg',
        },
      ],
    },
    connect: {
      title: 'Connect with Us',
      icons: [
        {
          name: 'facebook',
          url: 'https://www.facebook.com/airasiamove',
          icon: 'https://static.airasia.com/design/images/email/facebook-MOVE.svg',
        },
        {
          name: 'tiktok',
          url: 'https://www.tiktok.com/@airasiamove',
          icon: 'https://static.airasia.com/design/images/email/tiktok-MOVE.svg',
        },
        {
          name: 'x',
          url: 'https://twitter.com/airasia',
          icon: 'https://static.airasia.com/design/images/email/x-MOVE.svg',
        },
        {
          name: 'instagram',
          url: 'https://www.instagram.com/airasiamove/',
          icon: 'https://static.airasia.com/design/images/email/instagram-MOVE.svg',
        },
        {
          name: 'youtube',
          url: 'https://www.youtube.com/@airasia',
          icon: 'https://static.airasia.com/design/images/email/youtube-MOVE.svg',
        },
        {
          name: 'linkedin',
          url: 'https://www.linkedin.com/company/airasia-move/',
          icon: 'https://static.airasia.com/design/images/email/linkedin-MOVE.svg',
        },
      ],
    },
  },
  disclaimer: {
    text: `© ${new Date().getFullYear()} AirAsia Berhad (Malaysia). This website is owned and operated by Move Travel Sdn Bhd (form erly known as AirAsia Com Travel Sdn Bhd) (201301020508), a registered travel agency (KPK/LN License No: 8287). Your usage of this website indicates that you agree to be bound by our {terms}, {use} and {privacy}.\n\nThis site is protected by reCAPTCHA and the Google’s {tos} and {gprivacy} apply.`,
    links: {
      terms: {
        label: 'Terms and Conditions',
        url: 'https://www.airasia.com/aa/about-us/en/gb/terms-and-conditions.html',
      },
      use: {
        label: 'Terms of Use',
        url: 'https://www.airasia.com/aa/about-us/en/gb/terms-of-use.html',
      },
      privacy: {
        label: 'Privacy Statement',
        url: 'https://www.airasia.com/aa/about-us/en/gb/privacy-statement.html',
      },
      tos: {
        label: 'Terms of Service',
        url: 'https://policies.google.com/terms',
      },
      gprivacy: {
        label: 'Privacy Policy',
        url: 'https://policies.google.com/privacy',
      },
    },
  },
};
